import { useEffect, useRef, useState } from 'react'

import {
  MAX_FLOOR_PLAN_ICON_SIZE,
  MIN_FLOOR_PLAN_ICON_SIZE,
} from '../hooks/useFloorChart'
import { useFloorPlan } from '../hooks/useFloorPlan'
import { useIsEditMode } from '@/hooks/useIsEditMode'
import { useSelector } from 'react-redux'

import { Box, Flex } from '@chakra-ui/react'

import {
  AlarmIcon,
  CameraIcon,
  CheckCircleIcon,
  DoorIcon,
  EditIcon,
  GuardsIcon,
  TrashIcon,
} from '@hivewatch/dark'

import { DeviceType } from '@/graphql/generated/schemas'
import { selectMe } from '@/redux/me/meSlice'
import { selectFacilityDrawerOpen } from '@/redux/ui/uiSlice'
import { FacilityDrawer } from '@/redux/ui/uiSlice.types'
import { mixpanel, trackEvent } from '@/utils/analytics'

import { FLOOR_PLAN_ICON_SIZE_ANALYTICS } from '../constants/constants'
import { DarkDeviceIconSizeSlider } from '../dark/DarkDeviceIconSizeSlider'
import { useFloorChart, useFloorPosition } from '../hooks'
import { ConnectionI, DeviceI, Size } from '../types/types'
import { convertCoordinatesDOMtoSVG } from '../utils/chart'
import {
  mapCheckpointData,
  mapConnectionsData,
  mapDevicesData,
} from '../utils/floorMappedData'
import {
  FLOOR_PLAN_CHECKPOINT_ACTION_ICON_IDS,
  FLOOR_PLAN_CHECKPOINT_ACTION_ICON_SIZE,
  FloorPlanColorScheme,
  FloorPlanVisualVariant,
} from '../utils/floorPlanVisuals'
import { DeviceIconSizeSlider } from './DeviceIconSizeSlider'

interface FloorChartContainerIProps {
  chartSize: number[] | Size
  imageUrl: string
  imageSize: Size
  devices: DeviceI[]
  connections: any[]
  isGuardTour?: boolean
  visualVariant?: FloorPlanVisualVariant
  colorScheme?: FloorPlanColorScheme
}

export const FloorChartContainer = ({
  chartSize,
  imageUrl,
  imageSize,
  devices,
  connections,
  isGuardTour,
  visualVariant = 'legacy',
  colorScheme = 'dark',
}: FloorChartContainerIProps) => {
  const me = useSelector(selectMe)
  const svgRef = useRef(null)
  const mapImageRef = useRef(null)
  const mapGroupRef = useRef(null)

  const [mappedDevices, setMappedDevices] = useState<DeviceI[]>(null)
  const [mappedCheckpointDevices, setMappedCheckpointDevices] =
    useState<DeviceI[]>(null)
  const [mappedConnections, setMappedConnections] =
    useState<ConnectionI[]>(null)
  const [isRendered, setRendered] = useState(false)
  const [isZoomed, setZoomed] = useState(false)

  const drawerOpen: FacilityDrawer = useSelector(selectFacilityDrawerOpen)

  const isFacilitiesRebrandEnabled = visualVariant === 'rebrand'

  const shouldShowIconResizer = !(
    drawerOpen === 'checkpoints' || drawerOpen === 'tour-plans'
  )

  const { validateDevicePosition, validateMapPosition } =
    useFloorPosition(imageSize)

  const [chartWidth, chartHeight] = chartSize
  // Keep as old Edit Mode check!
  const isEditMode = useIsEditMode()
  const {
    floorId,
    facilityId,
    checkpointDevices,
    highlightedDeviceIds,
    tourStopDeviceIds,
    floorPlanIconSize,
    onIconResize,
    onFloorPlanContainerClick,
    onDeviceClicked,
    onDeviceDragStart,
    onDeviceDragged,
    onDeviceDragEnd,
    onDeviceHover,
    onDeviceHoverOut,
    forceCloseHoverCards,
    onMoveCheckPointClicked,
    onConnectionButtonClicked,
    onRemoveCheckpoint,
    handleDeviceDropped,
  } = useFloorPlan()

  // The slider reports every intermediate step through onChange and the released
  // value through onChangeEnd, so the size the gesture started from is captured
  // on the first step rather than held across renders — floorPlanIconSize also
  // changes on floor switch and on rehydration from redux, neither of which is
  // a user resize.
  const isResizingRef = useRef(false)
  const iconSizeAtDragStartRef = useRef(floorPlanIconSize)

  const handleIconResize = (newSize: number) => {
    if (!isResizingRef.current) {
      isResizingRef.current = true
      iconSizeAtDragStartRef.current = floorPlanIconSize
    }
    onIconResize(newSize)
  }

  const handleIconResizeEnd = (newSize: number) => {
    // onChangeEnd also fires on a click that never moved the thumb. Without a
    // preceding onChange there is no gesture, and iconSizeAtDragStartRef still
    // holds the *previous* drag's starting size — comparing against it would
    // report a second, fabricated resize.
    const didResize = isResizingRef.current
    isResizingRef.current = false
    if (!didResize) return

    const previousSize = iconSizeAtDragStartRef.current
    if (newSize === previousSize) return

    trackEvent(FLOOR_PLAN_ICON_SIZE_ANALYTICS.changed, {
      facility_id: facilityId,
      floor_id: floorId,
      icon_size: newSize,
      previous_icon_size: previousSize,
      direction: newSize > previousSize ? 'increase' : 'decrease',
      role: me.roleName,
    })
  }

  const onDrop = (e) => {
    e.stopPropagation()
    const { x, y } = convertCoordinatesDOMtoSVG(
      svgRef.current,
      mapGroupRef.current,
      e.clientX,
      e.clientY
    )
    const isValid = validateMapPosition(x, y)
    handleDeviceDropped([x, y], isValid, imageSize)
  }

  const refreshDevices = () => {
    const devicesData = mapDevicesData(devices)
    const connectionsData = mapConnectionsData(connections)
    const checkpointData = mapCheckpointData(checkpointDevices, floorId)
    setMappedDevices(devicesData)
    setMappedConnections(connectionsData)
    setMappedCheckpointDevices(checkpointData)
  }

  const { render, centerZoom } = useFloorChart({
    floorPlanIconSize: floorPlanIconSize,
    devices: mappedDevices,
    checkpoints: mappedCheckpointDevices,
    connections: mappedConnections,
    interactionsEnabled: true,
    onDeviceDragStart,
    onDeviceUpdated: (d: DeviceI) =>
      onDeviceDragged(d, validateDevicePosition(d)),
    onDeviceReleased: (d: DeviceI) => {
      onDeviceDragEnd(d, validateDevicePosition(d))
      if (d.deviceType === DeviceType.Checkpoint) {
        mixpanel.track('Button Press: Map Checkpoint - End Moving', {
          checkpoint_id: d.checkpoint.id,
          facility_id: d.checkpoint.facility.id,
          floor_id: d.checkpoint.floor.id,
          role: me.roleName,
        })
      }
    },
    onDeviceClicked: onDeviceClicked,
    onDeviceHover: onDeviceHover,
    onDeviceHoverOut: onDeviceHoverOut,
    onForceCloseHoverCards: forceCloseHoverCards,
    onConnectionButtonClicked: onConnectionButtonClicked,
    onMoveCheckpoint: (d: DeviceI) => {
      onMoveCheckPointClicked(d.id)
      if (!d.editCheckpointPos) {
        mixpanel.track('Button Press: Map Checkpoint - Move Placement', {
          checkpoint_id: d.checkpoint.id,
          facility_id: d.checkpoint.facility.id,
          floor_id: d.checkpoint.floor.id,
          role: me.roleName,
        })
      }
    },
    onRemoveCheckpoint: onRemoveCheckpoint,
    highlightedDeviceIds: highlightedDeviceIds,
    tourStopDeviceIds,
    visualVariant,
    colorScheme,
  })

  useEffect(() => {
    if (svgRef.current && !isRendered) {
      render(svgRef.current, mapImageRef.current)
      setRendered(true)
    }
  }, [svgRef])

  useEffect(() => {
    if (isRendered && imageSize) {
      centerZoom(svgRef.current, mapImageRef.current)
      setZoomed(true)
      refreshDevices()
    }
  }, [svgRef, imageSize, isRendered])

  useEffect(() => {
    if (!isRendered || !devices || !isZoomed) return
    refreshDevices()
  }, [devices, connections, imageSize, isRendered, isZoomed])

  return (
    <Box width={chartWidth}>
      <Flex
        onClick={onFloorPlanContainerClick}
        onDragOver={(e) => {
          e.preventDefault()
        }}
        onDrop={onDrop}
        width='100%'
      >
        <svg height={chartHeight} ref={svgRef} width={chartWidth}>
          <defs>
            <linearGradient id='device-shape-gradient' x2='0.35' y2='1'>
              <stop offset='0%' stopColor='var(--color-stop)' />
              <stop offset='30%' stopColor='var(--color-stop)' />
              <stop offset='100%' stopColor='var(--color-bot)' />
            </linearGradient>
            <g id='floor-plan-camera-glyph'>
              <CameraIcon color='currentColor' size={20} />
            </g>
            <g id='floor-plan-door-glyph'>
              <DoorIcon color='currentColor' size={20} />
            </g>
            <g id='floor-plan-alarm-glyph'>
              <AlarmIcon color='currentColor' size={20} />
            </g>
            <g id='floor-plan-guard-glyph'>
              <GuardsIcon color='currentColor' size={20} />
            </g>
            <g id={FLOOR_PLAN_CHECKPOINT_ACTION_ICON_IDS.remove.slice(1)}>
              <TrashIcon
                color='currentColor'
                size={FLOOR_PLAN_CHECKPOINT_ACTION_ICON_SIZE}
              />
            </g>
            <g id={FLOOR_PLAN_CHECKPOINT_ACTION_ICON_IDS.move.slice(1)}>
              <EditIcon
                color='currentColor'
                size={FLOOR_PLAN_CHECKPOINT_ACTION_ICON_SIZE}
              />
            </g>
            <g id={FLOOR_PLAN_CHECKPOINT_ACTION_ICON_IDS.endMoving.slice(1)}>
              <CheckCircleIcon
                color='currentColor'
                size={FLOOR_PLAN_CHECKPOINT_ACTION_ICON_SIZE}
              />
            </g>
          </defs>
          <g className='map-group' ref={mapGroupRef}>
            <image
              className='map'
              height={imageSize ? imageSize[1] : 0}
              href={imageUrl}
              ref={mapImageRef}
              width={imageSize ? imageSize[0] : 0}
              x={0}
              y={0}
            />
            {!isGuardTour ? (
              <>
                <g className='camera-tools-group'></g>
                <g className={`connections-group ${isEditMode && 'editOn'}`} />
                <g className={`camera-group ${isEditMode && 'editOn'}`} />
                <g className={`door-group ${isEditMode && 'editOn'}`} />
                <g className={`alarm-group ${isEditMode && 'editOn'}`} />
              </>
            ) : (
              <g className='checkpoint-group' />
            )}
          </g>
        </svg>
      </Flex>

      {shouldShowIconResizer && (
        <Box bottom='5' left='5' position='absolute' zIndex={10}>
          {isFacilitiesRebrandEnabled ? (
            <DarkDeviceIconSizeSlider
              max={MAX_FLOOR_PLAN_ICON_SIZE}
              min={MIN_FLOOR_PLAN_ICON_SIZE}
              onChange={handleIconResize}
              onChangeEnd={handleIconResizeEnd}
              value={floorPlanIconSize}
            />
          ) : (
            <DeviceIconSizeSlider
              max={MAX_FLOOR_PLAN_ICON_SIZE}
              min={MIN_FLOOR_PLAN_ICON_SIZE}
              onChange={onIconResize}
              value={floorPlanIconSize}
            />
          )}
        </Box>
      )}
    </Box>
  )
}
